<template>
  <div class="cv-page anim-fade-in-up">
    <!-- 主体：左侧栏 + 右侧内容 -->
    <div class="main-body">
      <!-- 左侧边栏 -->
      <div class="sidebar">
        <!-- 模型选择 -->
        <div class="panel">
          <span class="small-caps">Model</span>
          <div class="model-row">
            <el-select v-model="selectedModel" placeholder="请选择模型" size="small" style="flex:1;">
              <el-option v-for="m in modelOptions" :key="m.value" :label="m.label" :value="m.value" />
            </el-select>
          </div>
          <div class="model-actions">
            <el-button size="small" type="primary" @click="handleLoadModel" :loading="loadingModel" style="flex:1;">{{ yoloLoaded ? '重新加载模型' : '加载模型' }}</el-button>
            <el-button size="small" @click="clearModelCache">清除缓存</el-button>
          </div>
          <p class="model-status" :class="{ loaded: yoloLoaded }">{{ yoloLoaded ? '已加载模型：' + currentModelLabel : '当前未加载模型' }}</p>
        </div>

        <!-- 检测输入 -->
        <div class="panel">
          <span class="small-caps">Input</span>
          <div class="input-grid">
            <div class="input-item" :class="{ active: inputMode === 'image' }" @click="switchInput('image')" title="图片">
              <i class="el-icon-picture"></i>
            </div>
            <div class="input-item" :class="{ active: inputMode === 'video' }" @click="switchInput('video')" title="视频">
              <i class="el-icon-video-camera"></i>
            </div>
            <div class="input-item" :class="{ active: inputMode === 'camera' }" @click="switchInput('camera')" title="摄像头">
              <i class="el-icon-camera-solid"></i>
            </div>
            <div class="input-item" :class="{ active: inputMode === 'folder' }" @click="switchInput('folder')" title="文件夹">
              <i class="el-icon-folder-opened"></i>
            </div>
          </div>
          <el-button size="small" @click="resetInput" style="width:100%; margin-top:8px;">重置输入</el-button>

          <!-- 图片上传 -->
          <div v-if="inputMode === 'image'" style="margin-top:8px;">
            <el-upload :show-file-list="false" :before-upload="handleImageUpload" accept="image/*">
              <el-button size="small" style="width:100%;">选择图片</el-button>
            </el-upload>
            <p v-if="imageFile" class="input-hint">已选择：{{ imageFile.name }}</p>
          </div>

          <!-- 视频上传 -->
          <div v-if="inputMode === 'video'" style="margin-top:8px;">
            <el-upload :show-file-list="false" :before-upload="handleVideoUpload" accept="video/*">
              <el-button size="small" style="width:100%;">选择视频</el-button>
            </el-upload>
            <p v-if="videoFile" class="input-hint">已选择：{{ videoFile.name }}</p>
          </div>

          <!-- 文件夹上传 -->
          <div v-if="inputMode === 'folder'" style="margin-top:8px;">
            <input
              ref="folderInput"
              type="file"
              webkitdirectory
              multiple
              accept="image/*"
              style="display:none;"
              @change="onFolderSelected"
            />
            <el-button size="small" style="width:100%;" @click="$refs.folderInput.click()">选择图片文件夹</el-button>
            <p v-if="folderFiles.length > 0" class="input-hint">已选择 {{ folderFiles.length }} 张图片，当前第 {{ folderIndex + 1 }} 张</p>
          </div>
        </div>

        <!-- 检测参数 -->
        <div class="panel">
          <span class="small-caps">Parameters</span>
          <div class="slider-row">
            <div class="slider-label">
              <span>置信度:</span>
              <b>{{ confThreshold.toFixed(2) }}</b>
            </div>
            <el-slider v-model="confThreshold" :min="0" :max="1" :step="0.01" style="padding:0 2px;" />
          </div>
          <div class="slider-row">
            <div class="slider-label">
              <span>交并比:</span>
              <b>{{ iouThreshold.toFixed(2) }}</b>
            </div>
            <el-slider v-model="iouThreshold" :min="0" :max="1" :step="0.01" style="padding:0 2px;" />
          </div>
          <div class="toggle-row">
            <span>是否保存检测结果</span>
            <el-switch v-model="saveResults" active-color="#B8860B" />
          </div>
        </div>

        <!-- 检测结果统计 -->
        <div class="panel">
          <span class="small-caps">Results</span>
          <div class="stats-grid">
            <div class="stat-item">用时(s): <span class="stat-value">{{ detectTime.toFixed(3) }}</span></div>
            <div class="stat-item">FPS: <span class="stat-value">{{ detectFps.toFixed(2) }}</span></div>
            <div class="stat-item">颗粒数量: <span class="stat-value">{{ detectionResults.length }}</span></div>
            <div class="stat-item">检测进度: <span class="stat-value">{{ detectProgress }}%</span></div>
            <div class="stat-item">平均粒径: <span class="stat-value">{{ avgDiameter }} px</span></div>
            <div class="stat-item">粒径范围: <span class="stat-value">{{ diameterRange }}</span></div>
          </div>
          <el-button
            size="small"
            :disabled="detectionResults.length === 0 && !recordedVideoBlob && Object.keys(folderAllResults).length === 0"
            @click="downloadResults"
            style="width:100%; margin-top:6px;"
          >
            <i class="el-icon-download"></i> 下载检测结果
          </el-button>
        </div>

        <!-- 控制按钮 -->
        <div class="control-buttons">
          <el-button type="primary" @click="startDetection" :disabled="isDetecting" style="flex:1;">
            <i class="el-icon-video-play"></i> 开始检测
          </el-button>
          <el-button type="danger" @click="stopDetection" :disabled="!isDetecting" style="flex:1;">
            <i class="el-icon-video-pause"></i> 终止检测
          </el-button>

        </div>
      </div>

      <!-- 右侧主区域 -->
      <div class="content-area">
        <!-- 视频/图像显示 -->
        <div class="video-container" ref="videoContainer">
          <!-- 摄像头模式：HLS流 -->
          <video
            v-if="inputMode === 'camera' && streamActive"
            ref="cameraVideo"
            class="display-video"
            controls
            playsinline webkit-playsinline x5-playsinline
            crossorigin="anonymous"
          ></video>
          <!-- 图片模式 -->
          <img v-else-if="(inputMode === 'image' || inputMode === 'folder') && displaySrc" :src="displaySrc" class="display-media" />
          <!-- 视频模式 -->
          <video
            v-else-if="inputMode === 'video' && displaySrc"
            ref="uploadVideoRef"
            :src="displaySrc"
            class="display-video"
            controls
          ></video>
          <!-- 占位 -->
          <div v-else class="video-placeholder">{{ placeholderText }}</div>

          <!-- 文件夹导航：左右箭头 + 右下角计数 -->
          <template v-if="inputMode === 'folder' && folderFiles.length > 1">
            <div class="folder-arrow folder-arrow-left" :class="{ disabled: folderIndex <= 0 }" @click="prevImage">&#8249;</div>
            <div class="folder-arrow folder-arrow-right" :class="{ disabled: folderIndex >= folderFiles.length - 1 }" @click="nextImage">&#8250;</div>
            <div class="folder-counter">{{ folderIndex + 1 }} / {{ folderFiles.length }}</div>
          </template>

          <!-- 检测框叠加层 -->
          <div class="detection-overlay" v-if="detectionResults.length > 0 && showOverlay"
            :style="{
              left: overlayLeft + 'px', top: overlayTop + 'px',
              width: overlayW + 'px', height: overlayH + 'px'
            }">
            <div
              v-for="box in detectionResults"
              :key="box.id"
              class="bbox"
              :class="{ highlighted: hoveredBoxId === box.id }"
              :style="boxStyle(box)"
            >
              <span class="bbox-label" :style="{ background: boxColor(box.cls) }">
                {{ box.label }}
              </span>
            </div>
          </div>
        </div>

        <!-- 数据表格 -->
        <div class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th style="width:10%;">序号</th>
                <th style="width:15%;">置信度</th>
                <th style="width:15%;">粒径(px)</th>
                <th style="width:15%;">面积(px²)</th>
                <th style="width:45%;">坐标位置</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="detectionResults.length === 0">
                <td colspan="5" class="table-placeholder">暂无检测结果，请先选择输入源并执行检测。</td>
              </tr>
              <tr v-for="(r, idx) in detectionResults" :key="r.id"
                @mouseenter="hoveredBoxId = r.id"
                @mouseleave="hoveredBoxId = null"
                :class="{ 'row-hovered': hoveredBoxId === r.id }">
                <td>{{ idx + 1 }}</td>
                <td>{{ (r.conf * 100).toFixed(1) }}%</td>
                <td>{{ r.diameter }}</td>
                <td>{{ r.area }}</td>
                <td>x1:{{ r.x1 }} y1:{{ r.y1 }} x2:{{ r.x2 }} y2:{{ r.y2 }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 底部状态栏 -->
    <div class="status-bar">
      <div class="status-item">
        <span class="status-dot" :class="yoloLoaded ? 'green' : 'gray'"></span>
        <span>{{ yoloLoaded ? '模型已就绪' : '模型未加载' }}</span>
      </div>
      <div class="status-item">当前置信度: {{ confThreshold.toFixed(2) }}</div>
      <div class="status-item">交并比: {{ iouThreshold.toFixed(2) }}</div>
      <div class="status-item">粒径统计: {{ classSummary }}</div>
      <div class="status-item" v-if="saveResults">检测结果将保存到本地</div>
    </div>

  </div>
</template>

<script>

export default {
  data() {
    return {
      // 模型 — 从后端API动态获取
      modelOptions: [],
      selectedModel: null,
      currentModelName: null,
      currentClassNames: ['particle'],
      currentModelInputSize: 640,
      yoloLoaded: false, yoloSession: null, yoloInputSize: 640, loadingModel: false,
      // 输入
      inputMode: 'image',
      imageFile: null, videoFile: null, folderFiles: [],
      folderIndex: 0,
      folderAllResults: {},  // { index: [boxes] }
      displaySrc: null, displayType: null,
      // 参数
      confThreshold: 0.25, iouThreshold: 0.7, saveResults: true,
      // 检测
      isDetecting: false, detectTimer: null, detectInferBusy: false,
      detectionResults: [], recordedResults: [],
      detectTime: 0, detectFps: 0, detectProgress: 0, totalFrames: 0, processedFrames: 0,
      // 摄像头
      hlsInstance: null, streamActive: false, streamUrl: '',
      // 保存
      recordedVideoBlob: null, mediaRecorder: null, recordedChunks: [],
      showOverlay: true,
      hoveredBoxId: null,
      origW: 640, origH: 640,
      overlayW: 100, overlayH: 100, overlayLeft: 0, overlayTop: 0,
      inferOrigW: 640,
      inferOrigH: 640,
      overlayRect: { left: 0, top: 0, w: '100%', h: '100%' }
    }
  },
  computed: {
    currentModelLabel() {
      const f = this.modelOptions.find(m => m.value === this.currentModelName);
      return f ? f.label : (this.currentModelName || '未加载');
    },
    placeholderText() {
      const map = { image: '请选择图片文件', video: '请选择视频文件', camera: '请点击"开始检测"连接USB摄像头', folder: '请选择图片文件夹' };
      return map[this.inputMode] || '请选择检测输入源';
    },
    avgDiameter() {
      if (this.detectionResults.length === 0) return '—';
      const sum = this.detectionResults.reduce((s, r) => s + r.diameter, 0);
      return Math.round(sum / this.detectionResults.length);
    },
    diameterRange() {
      if (this.detectionResults.length === 0) return '—';
      const ds = this.detectionResults.map(r => r.diameter);
      return Math.min(...ds) + ' ~ ' + Math.max(...ds) + ' px';
    },
    classSummary() {
      if (this.detectionResults.length === 0) return '无';
      return `颗粒 ${this.detectionResults.length} 个, 均径 ${this.avgDiameter}px`;
    },
  },
  mounted() {
    this.fetchModelList();
  },
  beforeDestroy() {
    this.stopDetection();
    this.destroyHls();
    this.clearDisplaySrc();
  },
  methods: {
    // ========== 获取模型列表 ==========
    async fetchModelList() {
      try {
        const resp = await this.$axios.get('/api/models/list');
        if (resp.data.code === 0) {
          const models = resp.data.data || [];
          this.modelOptions = models.map(m => ({
            value: m.id,
            label: m.modelName,
            classNames: this.parseClassNames(m.classNames),
            inputSize: m.inputSize || 640
          }));
          // 默认选中活跃模型
          const active = models.find(m => m.isActive);
          if (active) {
            this.selectedModel = active.id;
          } else if (this.modelOptions.length > 0) {
            this.selectedModel = this.modelOptions[0].value;
          }
        }
      } catch (e) { console.warn('获取模型列表失败:', e); }
    },
    parseClassNames(cn) {
      if (!cn) return ['particle'];
      try {
        const arr = JSON.parse(cn);
        return Array.isArray(arr) && arr.length > 0 ? arr : ['particle'];
      } catch (e) { return cn.split(',').map(s => s.trim()).filter(Boolean); }
    },
    // ========== IndexedDB 缓存 ==========
    openModelCache() {
      return new Promise((resolve, reject) => {
        const req = indexedDB.open('HFLIBS_ModelCache', 1);
        req.onupgradeneeded = () => { req.result.createObjectStore('models'); };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });
    },
    async readModelCache(modelId) {
      try {
        const db = await this.openModelCache();
        return new Promise((resolve) => {
          const tx = db.transaction('models', 'readonly');
          const req = tx.objectStore('models').get(modelId);
          req.onsuccess = () => { db.close(); resolve(req.result || null); };
          req.onerror = () => { db.close(); resolve(null); };
        });
      } catch (e) { return null; }
    },
    async writeModelCache(modelId, buffer) {
      try {
        const db = await this.openModelCache();
        return new Promise((resolve) => {
          const tx = db.transaction('models', 'readwrite');
          tx.objectStore('models').put(buffer, modelId);
          tx.oncomplete = () => { db.close(); resolve(); };
          tx.onerror = () => { db.close(); resolve(); };
        });
      } catch (e) { /* 静默忽略 */ }
    },

    async clearModelCache() {
      try {
        const db = await this.openModelCache();
        const tx = db.transaction('models', 'readwrite');
        const store = tx.objectStore('models');
        const keys = await new Promise(resolve => {
          const req = store.getAllKeys();
          req.onsuccess = () => resolve(req.result || []);
          req.onerror = () => resolve([]);
        });
        keys.forEach(k => store.delete(k));
        tx.oncomplete = () => { db.close(); this.$message.success(`已清除 ${keys.length} 个模型缓存`); };
        tx.onerror = () => { db.close(); this.$message.error('清除失败'); };
      } catch (e) { this.$message.error('清除失败'); }
    },

    // ========== 模型加载（缓存优先） ==========
    async handleLoadModel() {
      if (this.loadingModel) return;
      if (!this.selectedModel) { this.$message.warning('请先选择一个模型'); return; }
      this.loadingModel = true;
      try {
        if (!window.ort) { this.$message.error('浏览器不支持视觉模型推理'); return; }
        const modelId = this.selectedModel;
        const option = this.modelOptions.find(m => m.value === modelId);
        const modelLabel = option ? option.label : ('模型 #' + modelId);
        if (this.yoloLoaded && this.currentModelName === modelLabel && this.yoloSession) {
          this.$message.success('模型已加载'); return;
        }

        // 设置类别名和输入尺寸（跟随选中模型）
        if (option) {
          this.currentClassNames = option.classNames || ['particle'];
          this.yoloInputSize = option.inputSize || 640;
          this.currentModelInputSize = option.inputSize || 640;
        }

        // ① 尝试从 IndexedDB 缓存加载
        this.yoloLoaded = false; this.yoloSession = null;
        const cached = await this.readModelCache(modelId);
        if (cached) {
          this.$message.success('模型加载完成');
          this.yoloSession = await window.ort.InferenceSession.create(cached, {
            executionProviders: ['webgpu', 'wasm']
          });
          this.yoloLoaded = true; this.currentModelName = modelLabel;
          return;
        }

        // ② 缓存未命中，从服务器下载
        this.$message.info(`正在加载模型：${modelLabel}，请稍候...`);
        const modelUrl = `/api/models/${modelId}/file`;
        const resp = await fetch(modelUrl);
        if (!resp.ok) throw new Error('下载失败');
        const buffer = await resp.arrayBuffer();

        // ③ 存入 IndexedDB
        await this.writeModelCache(modelId, buffer);

        // ④ 加载模型
        this.yoloSession = await window.ort.InferenceSession.create(buffer, {
          executionProviders: ['webgpu', 'wasm']
        });
        this.yoloLoaded = true; this.currentModelName = modelLabel;
        this.$message.success('模型加载完成');
      } catch (e) { console.error(e); this.$message.error('加载失败，请检查模型文件'); }
      finally { this.loadingModel = false; }
    },

    // ========== 输入切换 ==========
    switchInput(mode) {
      if (this.isDetecting) { this.$message.warning('请先终止检测'); return; }
      this.stopCameraStream();
      this.clearDisplaySrc();
      this.inputMode = mode;
      this.detectionResults = []; this.recordedResults = [];
      this.imageFile = null; this.videoFile = null; this.folderFiles = [];
      this.folderIndex = 0; this.folderAllResults = {};
      this.displaySrc = null;
    },
    resetInput() {
      if (this.isDetecting) { this.$message.warning('请先终止检测'); return; }
      this.stopCameraStream();
      this.clearDisplaySrc();
      this.detectionResults = []; this.recordedResults = [];
      this.imageFile = null; this.videoFile = null; this.folderFiles = [];
      this.folderIndex = 0; this.folderAllResults = {};
      this.displaySrc = null;
      this.detectTime = 0; this.detectFps = 0; this.detectProgress = 0;
      this.recordedVideoBlob = null; this.recordedChunks = [];
    },
    handleImageUpload(file) {
      this.clearDisplaySrc();
      this.imageFile = file; this.videoFile = null; this.folderFiles = [];
      this.displaySrc = URL.createObjectURL(file); this.displayType = 'image';
      this.detectionResults = [];
      return false;
    },
    handleVideoUpload(file) {
      this.clearDisplaySrc();
      this.videoFile = file; this.imageFile = null; this.folderFiles = [];
      this.displaySrc = URL.createObjectURL(file); this.displayType = 'video';
      this.detectionResults = [];
      this.$nextTick(() => { const v = this.$refs.uploadVideoRef; if (v && v.load) v.load(); });
      return false;
    },
    onFolderSelected(e) {
      const files = Array.from(e.target.files || []).filter(f => f.type.startsWith('image/'));
      if (files.length === 0) { this.$message.warning('未找到图片文件'); return; }
      this.clearDisplaySrc();
      this.folderFiles = files;
      this.folderIndex = 0;
      this.folderAllResults = {};
      this.detectionResults = [];
      this.recordedResults = [];
      this.displaySrc = URL.createObjectURL(files[0]);
      this.displayType = 'image';
      this.$message.success(`已加载 ${files.length} 张图片`);
      e.target.value = '';
    },
    async prevImage() {
      if (this.folderIndex <= 0) return;
      this.folderIndex--;
      await this.switchToFolderImage();
    },
    async nextImage() {
      if (this.folderIndex >= this.folderFiles.length - 1) return;
      this.folderIndex++;
      await this.switchToFolderImage();
    },
    async switchToFolderImage() {
      const idx = this.folderIndex;
      if (this.displaySrc && this.displaySrc.startsWith('blob:')) URL.revokeObjectURL(this.displaySrc);
      this.displaySrc = URL.createObjectURL(this.folderFiles[idx]);

      // 还原已缓存的检测结果（如果有），否则显示空
      this.detectionResults = this.folderAllResults[idx] || [];
    },

    // ========== 摄像头流 ==========
    async startCameraStream() {
      this.destroyHls();
      try {
        const resp = await this.$axios.get('/api/video/stream/url');
        if (resp.data.code !== 0) { this.$message.error('获取视频流地址失败'); return false; }
        this.streamUrl = resp.data.data.streamUrl;
        this.streamActive = true;
        await this.$nextTick();
        const videoEl = this.$refs.cameraVideo;
        if (!videoEl) { this.streamActive = false; return false; }
        if (window.Hls && window.Hls.isSupported()) {
          const hls = new window.Hls({ enableWorker: true, lowLatencyMode: false, debug: false });
          hls.loadSource(this.streamUrl); hls.attachMedia(videoEl);
          await new Promise((resolve, reject) => {
            hls.on(window.Hls.Events.MANIFEST_PARSED, () => { videoEl.controls = true; videoEl.play().catch(() => {}); resolve(); });
            hls.on(window.Hls.Events.ERROR, (_evt, data) => { if (data.fatal) { this.streamActive = false; reject(new Error('HLS加载失败')); } });
          });
          this.hlsInstance = hls;
        } else { videoEl.controls = true; videoEl.src = this.streamUrl; videoEl.load(); await videoEl.play().catch(() => {}); }
        return true;
      } catch (e) { console.error(e); this.streamActive = false; this.$message.error('摄像头连接失败'); return false; }
    },
    stopCameraStream() {
      this.destroyHls();
      const videoEl = this.$refs.cameraVideo;
      if (videoEl) { try { videoEl.pause(); } catch(e) {} videoEl.removeAttribute('src'); videoEl.load(); }
      this.streamActive = false; this.streamUrl = '';
    },
    destroyHls() { if (this.hlsInstance) { try { this.hlsInstance.destroy(); } catch(e) {} this.hlsInstance = null; } },
    clearDisplaySrc() {
      if (this.displaySrc && this.displaySrc.startsWith('blob:')) URL.revokeObjectURL(this.displaySrc);
      this.displaySrc = null; this.displayType = null;
    },

    // ========== 检测控制 ==========
    async startDetection() {
      if (!this.yoloLoaded) { this.$message.warning('请先加载模型'); return; }

      // 摄像头模式：启动流 + 若暂停则播放
      if (this.inputMode === 'camera') {
        if (!this.streamActive) {
          const ok = await this.startCameraStream();
          if (!ok) return;
          // 等视频有数据
          const videoEl = this.$refs.cameraVideo;
          if (videoEl && videoEl.readyState < 2) {
            await new Promise(resolve => {
              const check = () => { if (videoEl.readyState >= 2) resolve(); else setTimeout(check, 200); };
              check();
            });
          }
        } else {
          const ve = this.$refs.cameraVideo;
          if (ve && ve.paused) ve.play().catch(() => {});
        }
      }
      // 图片/文件夹模式：检查已选择
      if (this.inputMode === 'image' && !this.displaySrc) { this.$message.warning('请先选择图片'); return; }
      if (this.inputMode === 'video' && !this.displaySrc) { this.$message.warning('请先选择视频'); return; }

      this.isDetecting = true; this.showOverlay = true;
      this.processedFrames = 0;
      this._videoAnnotatedFrames = [];

      if (this.inputMode === 'camera') {
        // 开始录制
        this.startRecording();
        this.runCameraDetection();
      } else if (this.inputMode === 'video') {
        const videoEl = this.$refs.uploadVideoRef;
        if (!videoEl) { this.$message.error('未找到视频元素'); this.isDetecting = false; return; }
        try {
          if (videoEl.readyState < 2) await new Promise(resolve => { videoEl.addEventListener('loadeddata', resolve, { once: true }); });
          videoEl.play().catch(() => {});
        } catch(e) { console.warn(e); }
        this.runVideoDetection(videoEl);
      } else if (this.inputMode === 'image') {
        this.processedFrames = 1; this.totalFrames = 1; this.detectProgress = 100;
        await this.runSingleDetection();
        this.isDetecting = false;
      } else if (this.inputMode === 'folder') {
        await this.runFolderDetection();
        this.isDetecting = false;
      }
    },

    stopDetection() {
      this.isDetecting = false;
      if (this.detectTimer) { clearTimeout(this.detectTimer); this.detectTimer = null; }
      this.detectionResults = [];
      // 摄像头模式：暂停视频
      if (this.inputMode === 'camera') {
        const ve = this.$refs.cameraVideo;
        if (ve) ve.pause();
      }
      this.stopRecording();
    },

    // ========== 单张图片检测 ==========
    async runSingleDetection() {
      const startTime = performance.now();
      const src = this.displaySrc;
      if (!src) return;
      try {
        const boxes = await this.inferImage(src);
        this.getOverlayMetrics();
        this.detectionResults = boxes;
        this.recordedResults.push(...boxes);
        this.detectTime = (performance.now() - startTime) / 1000;
        this.detectFps = 1 / this.detectTime;
        if (this.saveResults) this.recordedResults = [...this.detectionResults];
      } catch(e) { console.error(e); this.$message.error('检测失败'); }
    },

    // ========== 本地视频检测 ==========
    runVideoDetection(videoEl) {
      const size = this.yoloInputSize;
      const canvas = document.createElement('canvas'); canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext('2d');
      const dur = videoEl.duration;
      this.totalFrames = (dur && isFinite(dur) && dur > 0) ? Math.floor(dur * 5) : 300;
      this.recordedResults = [];
      this._videoAnnotatedFrames = [];  // 存储标注帧 Blob
      // 用 requestVideoFrameCallback 对齐视频帧刷新
      const useVFC = typeof videoEl.requestVideoFrameCallback === 'function';
      let lastDetectTime = 0;
      const detect = async (_now, _metadata) => {
        if (!this.isDetecting || videoEl.ended) { this.stopDetection(); return; }
        const now = performance.now();
        if (now - lastDetectTime < 60) {
          if (useVFC) videoEl.requestVideoFrameCallback(detect);
          else this.detectTimer = setTimeout(detect, 30);
          return;
        }
        lastDetectTime = now;
        try {
          const startTime = performance.now();
          this.origW = videoEl.videoWidth || 640; this.origH = videoEl.videoHeight || 480;
          const lb2 = this.drawToCanvas(ctx, videoEl, size, this.origW, this.origH);
          const boxes = await this.inferCanvas(ctx, size, lb2);
          this.detectionResults = boxes;
          if (this.saveResults && this.recordedResults.length < 5000) {
            const frameIdx = this._videoAnnotatedFrames.length;
            boxes.forEach(b => b._frame = frameIdx);
            this.recordedResults.push(...boxes);
            // 生成标注帧
            const vw = this.origW, vh = this.origH;
            const fCanvas = document.createElement('canvas'); fCanvas.width = vw; fCanvas.height = vh;
            const fCtx = fCanvas.getContext('2d'); fCtx.drawImage(videoEl, 0, 0, vw, vh);
            fCtx.strokeStyle = '#27ae60'; fCtx.lineWidth = Math.max(2, Math.round(vw / 400));
            fCtx.fillStyle = '#27ae60'; fCtx.font = `bold ${Math.max(12, Math.round(vw / 60))}px sans-serif`;
            boxes.forEach(b => {
              const bx = b.x1 / 100 * vw, by = b.y1 / 100 * vh;
              const bw = (b.x2 - b.x1) / 100 * vw, bh = (b.y2 - b.y1) / 100 * vh;
              fCtx.strokeRect(bx, by, bw, bh);
              const label = `P${b.id}`; const tm = fCtx.measureText(label); const lh = Math.max(14, Math.round(vw / 60)) + 4;
              fCtx.fillRect(bx, by - lh, tm.width + 6, lh);
              fCtx.fillStyle = '#fff'; fCtx.fillText(label, bx + 3, by - 4); fCtx.fillStyle = '#27ae60';
            });
            this._videoAnnotatedFrames.push(fCanvas.toDataURL('image/jpeg', 0.8));
          }
          this.processedFrames++;
          this.detectProgress = Math.min(99, Math.round(videoEl.currentTime / (dur || 60) * 100));
          this.detectTime = (performance.now() - startTime) / 1000;
          this.detectFps = this.detectTime > 0 ? 1 / this.detectTime : 0;
        } catch(e) { console.error(e); }
        if (this.isDetecting) {
          if (useVFC) videoEl.requestVideoFrameCallback(detect);
          else this.detectTimer = setTimeout(detect, 150);
        }
      };
      if (useVFC) videoEl.requestVideoFrameCallback(detect);
      else this.detectTimer = setTimeout(detect, 300);
    },

    // ========== 摄像头检测 ==========
    runCameraDetection() {
      const videoEl = this.$refs.cameraVideo;
      if (!videoEl) { this.stopDetection(); return; }
      const size = this.yoloInputSize;
      const canvas = document.createElement('canvas'); canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext('2d');
      let frameIdx = 0;
      let lastDetectTime = 0;
      const detect = async () => {
        if (!this.isDetecting) return;
        if (videoEl.readyState < 2) { this.detectTimer = setTimeout(detect, 100); return; }
        const now = performance.now();
        if (now - lastDetectTime < 80) { this.detectTimer = setTimeout(detect, 40); return; }
        lastDetectTime = now;
        try {
          const startTime = performance.now();
          this.origW = videoEl.videoWidth || 640; this.origH = videoEl.videoHeight || 480;
          const lb2 = this.drawToCanvas(ctx, videoEl, size, this.origW, this.origH);
          const boxes = await this.inferCanvas(ctx, size, lb2);
          this.detectionResults = boxes;
          if (this.saveResults && this.recordedResults.length < 5000) {
            const cfIdx = (this._videoAnnotatedFrames || []).length;
            boxes.forEach(b => b._frame = cfIdx);
            this.recordedResults.push(...boxes);
            // 生成标注帧
            const vw = this.origW, vh = this.origH;
            const fCanvas = document.createElement('canvas'); fCanvas.width = vw; fCanvas.height = vh;
            const fCtx = fCanvas.getContext('2d'); fCtx.drawImage(videoEl, 0, 0, vw, vh);
            fCtx.strokeStyle = '#27ae60'; fCtx.lineWidth = Math.max(2, Math.round(vw / 400));
            fCtx.fillStyle = '#27ae60'; fCtx.font = `bold ${Math.max(12, Math.round(vw / 60))}px sans-serif`;
            boxes.forEach(b => {
              const bx = b.x1 / 100 * vw, by = b.y1 / 100 * vh;
              const bw = (b.x2 - b.x1) / 100 * vw, bh = (b.y2 - b.y1) / 100 * vh;
              fCtx.strokeRect(bx, by, bw, bh);
              const label = `P${b.id}`; const tm = fCtx.measureText(label); const lh = Math.max(14, Math.round(vw / 60)) + 4;
              fCtx.fillRect(bx, by - lh, tm.width + 6, lh);
              fCtx.fillStyle = '#fff'; fCtx.fillText(label, bx + 3, by - 4); fCtx.fillStyle = '#27ae60';
            });
            if (!this._videoAnnotatedFrames) this._videoAnnotatedFrames = [];
            this._videoAnnotatedFrames.push(fCanvas.toDataURL('image/jpeg', 0.8));
          }
          frameIdx++;
          this.processedFrames = frameIdx;
          this.totalFrames = frameIdx + 1;
          this.detectProgress = 100;
          const elapsed = (performance.now() - startTime) / 1000;
          this.detectTime = elapsed;
          this.detectFps = elapsed > 0 ? 1 / elapsed : 0;
        } catch(e) { console.error(e); }
        this.detectTimer = setTimeout(detect, 100);
      };
      this.detectTimer = setTimeout(detect, 300);
    },

    // ========== 文件夹批量检测 ==========
    async runFolderDetection() {
      const files = this.folderFiles;
      this.totalFrames = files.length;
      this.folderAllResults = {};
      this.recordedResults = [];
      for (let i = 0; i < files.length; i++) {
        if (!this.isDetecting) break;
        const url = URL.createObjectURL(files[i]);
        const startTime = performance.now();
        try {
          const boxes = await this.inferImage(url);
          this.folderAllResults[i] = boxes;
          this.recordedResults.push(...boxes);
          this.folderIndex = i;
          this.detectionResults = boxes;
          if (this.displaySrc && this.displaySrc.startsWith('blob:')) URL.revokeObjectURL(this.displaySrc);
          this.displaySrc = url;  // 复用 URL，不额外创建
          this.displayType = 'image';
          this.processedFrames = i + 1;
          this.detectProgress = Math.round(this.processedFrames / this.totalFrames * 100);
          this.detectTime = (performance.now() - startTime) / 1000;
          this.detectFps = this.detectTime > 0 ? 1 / this.detectTime : 0;
        } catch(e) { console.error(e); if (url) URL.revokeObjectURL(url); }
        await new Promise(r => setTimeout(r, 50));
      }
    },

    // ========== YOLO推理核心 ==========
    inferCanvas(ctx, size, lb) {
      return new Promise((resolve, reject) => {
        try {
          const imageData = ctx.getImageData(0, 0, size, size);
          const { data } = imageData;
          const input = new Float32Array(3 * size * size);
          const area = size * size;
          for (let i = 0, px = 0; i < data.length; i += 4, px++) {
            input[px] = data[i] / 255;
            input[area + px] = data[i + 1] / 255;
            input[2 * area + px] = data[i + 2] / 255;
          }
          this.runInference(input, size, lb).then(resolve).catch(reject);
        } catch(e) { reject(e); }
      });
    },
    drawToCanvas(ctx, source, size, ow, oh) {
      const scale = size / Math.max(ow, oh);
      const dw = ow * scale, dh = oh * scale;
      const dx = (size - dw) / 2, dy = (size - dh) / 2;
      ctx.fillStyle = '#727272';
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(source, dx, dy, dw, dh);
      // 返回 letterbox 参数供反向换算
      return { scale, padX: dx, padY: dy };
    },
    inferImage(src) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = async () => {
          try {
            const size = this.yoloInputSize;
            this.origW = img.naturalWidth;
            this.origH = img.naturalHeight;
            const canvas = document.createElement('canvas'); canvas.width = size; canvas.height = size;
            const ctx = canvas.getContext('2d');
            const lb = this.drawToCanvas(ctx, img, size, this.origW, this.origH);
            const boxes = await this.inferCanvas(ctx, size, lb);
            resolve(boxes);
          } catch(e) { reject(e); }
        };
        img.onerror = reject;
        img.src = src;
      });
    },
    async runInference(input, size, lb) {
      const tensor = new window.ort.Tensor('float32', input, [1, 3, size, size]);
      const results = await this.yoloSession.run({ images: tensor });
      const output = results[Object.keys(results)[0]];
      const outData = output.data; const dims = output.dims;
      if (dims.length !== 3 || dims[2] < 6) return [];

      const numDet = dims[1]; const stride = dims[2];
      const { scale, padX, padY } = lb;
      const ow = this.origW, oh = this.origH;

      const rawBoxes = [];
      for (let i = 0; i < numDet; i++) {
        const base = i * stride;
        const score = outData[base + 4];
        if (score < 0.05) continue;
        const clsId = Math.round(outData[base + 5]);
        // 模型空间(letterboxed 640) → 原始图像空间
        const ox1 = (outData[base] - padX) / scale;
        const oy1 = (outData[base + 1] - padY) / scale;
        const ox2 = (outData[base + 2] - padX) / scale;
        const oy2 = (outData[base + 3] - padY) / scale;
        // 原始图像百分比
        const x1 = Math.round(ox1 / ow * 100);
        const y1 = Math.round(oy1 / oh * 100);
        const x2 = Math.round(ox2 / ow * 100);
        const y2 = Math.round(oy2 / oh * 100);
        const bw = x2 - x1, bh = y2 - y1;
        if (bw <= 0 || bh <= 0) continue;
        const wPx = ox2 - ox1, hPx = oy2 - oy1;
        const areaPx = Math.round(wPx * hPx);
        const diameterPx = Math.round((wPx + hPx) / 2);
        rawBoxes.push({ x1, y1, x2, y2, conf: score, cls: clsId, wPx, hPx, areaPx, diameterPx });
      }

      // 第二轮：几何过滤（与 CV 2.0.1 完全一致）
      const AREA_MIN = 150, AREA_MAX = 30000;
      const ASPECT_MIN = 0.5, ASPECT_MAX = 2.0;
      const filtered = rawBoxes.filter(b => {
        if (b.conf < this.confThreshold) return false;
        if (b.areaPx < AREA_MIN || b.areaPx > AREA_MAX) return false;
        const aspect = b.wPx / b.hPx;
        if (aspect < ASPECT_MIN || aspect > ASPECT_MAX) return false;
        return true;
      });

      // 第三轮：重新编号
      return filtered.map((b, idx) => ({
        id: idx + 1,
        x1: b.x1, y1: b.y1, x2: b.x2, y2: b.y2,
        conf: b.conf, cls: b.cls,
        label: 'P' + (idx + 1),
        area: Math.round(b.areaPx),
        diameter: b.diameterPx
      }));
    },

    // ========== 录制 ==========
    startRecording() {
      if (!this.saveResults) return;
      const videoEl = this.$refs.cameraVideo;
      if (!videoEl) return;
      const cs = videoEl.captureStream ? videoEl.captureStream() : null;
      if (!cs) return;
      if (typeof MediaRecorder === 'undefined') return;
      try {
        let opts = {};
        if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9')) opts.mimeType = 'video/webm;codecs=vp9';
        else if (MediaRecorder.isTypeSupported('video/webm')) opts.mimeType = 'video/webm';
        this.recordedChunks = [];
        this.mediaRecorder = new MediaRecorder(cs, opts);
        this.mediaRecorder.ondataavailable = e => { if (e.data && e.data.size > 0) this.recordedChunks.push(e.data); };
        this.mediaRecorder.onstop = () => {
          if (this.recordedChunks.length > 0) this.recordedVideoBlob = new Blob(this.recordedChunks, { type: 'video/webm' });
        };
        this.mediaRecorder.start(1000);
      } catch(e) { console.warn('录制初始化失败:', e); }
    },
    stopRecording() {
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        try { this.mediaRecorder.stop(); } catch(e) {}
        this.mediaRecorder = null;
      }
    },

    // ========== 下载 ==========
    async downloadResults() {
      const now = new Date();
      const ts = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}_${String(now.getHours()).padStart(2,'0')}${String(now.getMinutes()).padStart(2,'0')}${String(now.getSeconds()).padStart(2,'0')}`;

      const bom = '﻿';
      let csvHeader, csvRows, csv;
      if (this.inputMode === 'video') {
        csvHeader = '帧序号,颗粒序号,置信度,粒径(px),面积(px²),坐标位置\n';
        csvRows = this.recordedResults.map((r, i) =>
          `${(r._frame || 0) + 1},${i + 1},${(r.conf * 100).toFixed(1)}%,${r.diameter},${r.area},"x1:${r.x1} y1:${r.y1} x2:${r.x2} y2:${r.y2}"`
        ).join('\n');
      } else {
        csvHeader = '序号,置信度,粒径(px),面积(px²),坐标位置\n';
        csvRows = this.recordedResults.map((r, i) =>
          `${i + 1},${(r.conf * 100).toFixed(1)}%,${r.diameter},${r.area},"x1:${r.x1} y1:${r.y1} x2:${r.x2} y2:${r.y2}"`
        ).join('\n');
      }
      csv = bom + csvHeader + csvRows;

      // 图片模式：生成带标注结果的图片 + CSV → ZIP
      if (this.inputMode === 'image' && this.displaySrc && this.detectionResults.length > 0) {
        try {
          const img = await new Promise((resolve, reject) => {
            const i = new Image();
            i.onload = () => resolve(i);
            i.onerror = reject;
            i.src = this.displaySrc;
          });
          const cw = img.naturalWidth, ch = img.naturalHeight;
          const canvas = document.createElement('canvas');
          canvas.width = cw; canvas.height = ch;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);

          // 画检测框和标签
          ctx.strokeStyle = '#27ae60'; ctx.lineWidth = Math.max(2, Math.round(cw / 400));
          ctx.fillStyle = '#27ae60'; ctx.font = `bold ${Math.max(12, Math.round(cw / 60))}px sans-serif`;
          this.detectionResults.forEach(b => {
            const bx = b.x1 / 100 * cw, by = b.y1 / 100 * ch;
            const bw = (b.x2 - b.x1) / 100 * cw, bh = (b.y2 - b.y1) / 100 * ch;
            ctx.strokeRect(bx, by, bw, bh);
            const label = `P${b.id}`;
            const tm = ctx.measureText(label);
            const lh = Math.max(14, Math.round(cw / 60)) + 4;
            ctx.fillRect(bx, by - lh, tm.width + 6, lh);
            ctx.fillStyle = '#fff';
            ctx.fillText(label, bx + 3, by - 4);
            ctx.fillStyle = '#27ae60';
          });

          const imgBlob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));

          if (window.JSZip) {
            const zip = new window.JSZip();
            zip.file(`detected_image.png`, imgBlob);
            zip.file(`detection_results.csv`, csv);
            const zipBlob = await zip.generateAsync({ type: 'blob' });
            this.downloadBlob(zipBlob, `detection_${ts}.zip`, 'application/zip');
            this.$message.success('检测结果已下载（含标注图+CSV）');
          } else {
            this.downloadBlob(csv, `detection_results.csv`, 'text/csv');
            this.downloadBlob(imgBlob, `detected_image.png`, 'image/png');
            this.$message.success('检测结果已下载');
          }
        } catch (e) { console.error(e); this.$message.error('生成结果图失败'); }
        return;
      }

      // 文件夹模式：所有标注图 + CSV → ZIP
      if (this.inputMode === 'folder' && this.folderFiles.length > 0 && Object.keys(this.folderAllResults).length > 0) {
        try {
          const zip = window.JSZip ? new window.JSZip() : null;
          // 汇总 CSV
          let allCsv = bom + '图片,颗粒序号,置信度,粒径(px),面积(px²),坐标位置\n';
          for (let fi = 0; fi < this.folderFiles.length; fi++) {
            const file = this.folderFiles[fi];
            const boxes = this.folderAllResults[fi];
            if (!boxes || boxes.length === 0) continue;
            // 生成标注图
            const url = URL.createObjectURL(file);
            const img = await new Promise((resolve, reject) => {
              const im = new Image(); im.onload = () => resolve(im); im.onerror = reject; im.src = url;
            });
            const cw = img.naturalWidth, ch = img.naturalHeight;
            const canvas = document.createElement('canvas');
            canvas.width = cw; canvas.height = ch;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            URL.revokeObjectURL(url);
            ctx.strokeStyle = '#27ae60'; ctx.lineWidth = Math.max(2, Math.round(cw / 400));
            ctx.fillStyle = '#27ae60'; ctx.font = `bold ${Math.max(12, Math.round(cw / 60))}px sans-serif`;
            boxes.forEach(b => {
              const bx = b.x1 / 100 * cw, by = b.y1 / 100 * ch;
              const bw = (b.x2 - b.x1) / 100 * cw, bh = (b.y2 - b.y1) / 100 * ch;
              ctx.strokeRect(bx, by, bw, bh);
              const label = `P${b.id}`;
              const tm = ctx.measureText(label);
              const lh = Math.max(14, Math.round(cw / 60)) + 4;
              ctx.fillRect(bx, by - lh, tm.width + 6, lh);
              ctx.fillStyle = '#fff';
              ctx.fillText(label, bx + 3, by - 4);
              ctx.fillStyle = '#27ae60';
              allCsv += `${file.name},${b.id},${(b.conf*100).toFixed(1)}%,${b.diameter},${b.area},"x1:${b.x1} y1:${b.y1} x2:${b.x2} y2:${b.y2}"\n`;
            });
            const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
            if (zip) {
              zip.file(`images/${file.name.replace(/\.\w+$/, '')}_detected.png`, blob);
            } else {
              this.downloadBlob(blob, `${file.name.replace(/\.\w+$/, '')}_detected.png`, 'image/png');
            }
          }
          if (zip) {
            zip.file('detection_results.csv', allCsv);
            const zipBlob = await zip.generateAsync({ type: 'blob' });
            this.downloadBlob(zipBlob, `folder_detection_${ts}.zip`, 'application/zip');
          } else {
            this.downloadBlob(allCsv, 'detection_results.csv', 'text/csv');
          }
          this.$message.success(`已下载 ${Object.keys(this.folderAllResults).length} 张标注图 + CSV`);
        } catch (e) { console.error(e); this.$message.error('打包下载失败'); }
        return;
      }

      // 本地视频 / 摄像头模式：标注帧 + CSV → ZIP
      const videoFrames = this._videoAnnotatedFrames;
      if ((this.inputMode === 'video' || this.inputMode === 'camera') && Array.isArray(videoFrames) && videoFrames.length > 0) {
        const blobs = videoFrames.map(dataUrl => {
          const arr = dataUrl.split(','), mime = arr[0].match(/:(.*?);/)[1];
          const bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
          for (let i = 0; i < n; i++) u8arr[i] = bstr.charCodeAt(i);
          return new Blob([u8arr], { type: mime });
        });
        if (window.JSZip) {
          const zip = new window.JSZip();
          blobs.forEach((blob, i) => { zip.file(`frame_${String(i+1).padStart(4,'0')}.jpg`, blob); });
          zip.file('detection_results.csv', csv);
          const zipBlob = await zip.generateAsync({ type: 'blob' });
          this.downloadBlob(zipBlob, `video_detection_${ts}.zip`, 'application/zip');
          this.$message.success(`已下载 ${blobs.length} 帧标注图 + CSV`);
        } else {
          this.downloadBlob(csv, 'detection_results.csv', 'text/csv');
          blobs.forEach((blob, i) => this.downloadBlob(blob, `frame_${String(i+1).padStart(4,'0')}.jpg`, 'image/jpeg'));
          this.$message.success(`已下载 ${blobs.length} 帧 + CSV`);
        }
        return;
      }
      // 视频/摄像头兜底：仅 CSV
      if (this.inputMode === 'video' || this.inputMode === 'camera') {
        this.downloadBlob(csv, `detections_${ts}.csv`, 'text/csv');
        this.$message.success(`已下载 CSV（${this.recordedResults.length} 条检测记录）`);
        return;
      }

      // 摄像头模式：CSV + 录制视频 → ZIP
      if (this.recordedVideoBlob) {
        if (window.JSZip) {
          const zip = new window.JSZip();
          zip.file(`detections_${ts}.csv`, csv);
          zip.file(`session_video.webm`, this.recordedVideoBlob);
          zip.generateAsync({ type: 'blob' }).then(blob => {
            this.downloadBlob(blob, `session_${ts}.zip`, 'application/zip');
            this.$message.success('检测结果已下载');
          });
        } else {
          this.downloadBlob(csv, `detections_${ts}.csv`, 'text/csv');
          this.downloadBlob(this.recordedVideoBlob, `session_video.webm`, 'video/webm');
          this.$message.success('检测结果已下载');
        }
      } else {
        this.downloadBlob(csv, `detections_${ts}.csv`, 'text/csv');
        this.$message.success('检测结果已下载');
      }
    },
    downloadBlob(data, filename, mime) {
      const blob = typeof data === 'string' ? new Blob([data], { type: mime + ';charset=utf-8;' }) : data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    },

    getOverlayMetrics() {
      const c = this.$refs.videoContainer;
      if (c && this.origW && this.origH) {
        const s = Math.min(c.clientWidth / this.origW, c.clientHeight / this.origH);
        this.overlayW = Math.round(this.origW * s);
        this.overlayH = Math.round(this.origH * s);
        this.overlayLeft = Math.round((c.clientWidth - this.overlayW) / 2);
        this.overlayTop = Math.round((c.clientHeight - this.overlayH) / 2);
      }
    },
    boxStyle(box) {
      this.getOverlayMetrics();
      return {
        left: Math.round(box.x1 / 100 * this.overlayW) + 'px',
        top: Math.round(box.y1 / 100 * this.overlayH) + 'px',
        width: Math.round((box.x2 - box.x1) / 100 * this.overlayW) + 'px',
        height: Math.round((box.y2 - box.y1) / 100 * this.overlayH) + 'px',
        borderColor: this.boxColor()
      };
    },
    boxColor() { return '#27ae60'; },
    updateOverlayRect() {
      const c = this.$refs.videoContainer;
      if (!c || !this.inferOrigW || !this.inferOrigH) {
        this.overlayRect = { left: 0, top: 0, w: '100%', h: '100%' };
        return;
      }
      const cw = c.clientWidth, ch = c.clientHeight;
      const scale = Math.min(cw / this.inferOrigW, ch / this.inferOrigH);
      const rw = this.inferOrigW * scale, rh = this.inferOrigH * scale;
      this.overlayRect = {
        left: Math.round((cw - rw) / 2),
        top: Math.round((ch - rh) / 2),
        w: Math.round(rw),
        h: Math.round(rh)
      };
    }
  }
}
</script>

<style scoped>
.page-title {
  font-family: var(--font-display);
  font-size: 1.5rem; font-weight: 500;
  color: var(--text-primary); margin: 0 0 20px; letter-spacing: 0.04em;
}

/* ---- 主体左右分栏 ---- */
.main-body {
  display: flex;
  gap: 14px;
  align-items: flex-start;
}

/* ---- 左侧边栏 ---- */
.sidebar {
  width: 260px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.panel {
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  padding: 12px 14px;
}
.panel > .small-caps {
  display: block;
  margin-bottom: 8px;
}

/* 模型 */
.model-row { display: flex; gap: 8px; align-items: center; }
.model-actions { display: flex; gap: 6px; margin-top: 8px; }
.model-status { margin-top: 8px; font-size: 0.75rem; color: var(--text-muted); }
.model-status.loaded { color: var(--accent-primary); }

/* 输入图标网格 */
.input-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
}
.input-item {
  aspect-ratio: 1;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all var(--transition-fast);
  background: var(--bg-elevated);
  color: var(--text-muted);
}
.input-item:hover { border-color: var(--accent-primary); color: var(--accent-primary); }
.input-item.active { border-color: var(--accent-primary); color: var(--accent-primary); background: var(--accent-primary-soft); }
.input-hint { font-size: 0.72rem; color: var(--text-muted); margin-top: 6px; word-break: break-all; }

/* 参数滑块 */
.slider-row { margin-bottom: 4px; }
.slider-label { display: flex; justify-content: space-between; font-size: 0.78rem; color: var(--text-secondary); margin-bottom: 2px; }
.slider-label b { color: var(--accent-primary); }
.toggle-row { display: flex; align-items: center; justify-content: space-between; font-size: 0.78rem; color: var(--text-secondary); margin-top: 6px; }

/* 统计 */
.stats-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.stat-item { font-size: 0.78rem; color: var(--text-secondary); }
.stat-value { color: var(--text-primary); font-weight: 600; }

/* 控制按钮 */
.control-buttons { display: flex; gap: 8px; }

/* 文件夹导航箭头 */
.folder-arrow {
  position: absolute; top: 50%; transform: translateY(-50%);
  width: 40px; height: 60px;
  display: flex; align-items: center; justify-content: center;
  font-size: 2rem; color: rgba(255,255,255,0.7);
  background: rgba(0,0,0,0.25); cursor: pointer;
  z-index: 10; user-select: none;
  transition: all 0.15s;
}
.folder-arrow:hover { background: rgba(0,0,0,0.5); color: #fff; }
.folder-arrow.disabled { opacity: 0.2; cursor: default; pointer-events: none; }
.folder-arrow-left  { left: 0; border-radius: 0 6px 6px 0; }
.folder-arrow-right { right: 0; border-radius: 6px 0 0 6px; }
.folder-counter {
  position: absolute; bottom: 10px; right: 14px;
  font-size: 0.78rem; color: rgba(255,255,255,0.8);
  font-family: var(--font-mono); background: rgba(0,0,0,0.4);
  padding: 2px 10px; border-radius: 10px; z-index: 10;
  pointer-events: none;
}

/* ---- 右侧内容 ---- */
.content-area {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.video-container {
  height: 620px;
  background: #1A1A1A;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-placeholder {
  color: rgba(255,255,255,0.6);
  font-size: 0.95rem;
  text-align: center;
}

.display-media, .display-video {
  max-width: 100%; max-height: 100%;
  object-fit: contain;
}

/* 检测框叠加层 */
.detection-overlay {
  position: absolute;
  top: 0; left: 0;
  width: 100%; height: 100%;
  pointer-events: none;
}
.bbox {
  position: absolute;
  border: 2px solid;
  border-radius: 2px;
  transition: left 0.3s ease-out, top 0.3s ease-out, width 0.3s ease-out, height 0.3s ease-out;
}
.bbox.highlighted {
  border-color: #ff6b35 !important;
  border-width: 4px !important;
  box-shadow: 0 0 14px rgba(255, 107, 53, 0.7);
  z-index: 5;
}
.bbox.highlighted .bbox-label {
  background: #ff6b35 !important;
}

.bbox-label {
  position: absolute;
  top: -20px; left: 0;
  padding: 1px 6px;
  font-size: 10px; color: #fff;
  border-radius: 2px;
  white-space: nowrap;
  font-weight: 600;
}

.table-container .data-table tbody tr.row-hovered {
  background: rgba(39, 174, 96, 0.08) !important;
}

/* 数据表格 */
.table-container {
  height: 160px;
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  overflow-y: auto;
}
.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.78rem;
  table-layout: fixed;
}
.data-table thead th {
  background: var(--bg-elevated);
  color: var(--accent-primary);
  font-weight: 600;
  padding: 8px;
  text-align: center;
  border-bottom: 2px solid var(--accent-primary);
  font-family: var(--font-display);
  font-size: 0.72rem;
  letter-spacing: 0.04em;
  position: sticky;
  top: 0;
  z-index: 1;
}
.data-table td {
  padding: 7px 8px;
  text-align: center;
  color: var(--text-secondary);
  border-bottom: 1px solid var(--border-subtle);
  font-family: var(--font-mono);
  font-size: 0.7rem;
}
.data-table tr:hover td { background: var(--accent-primary-soft); }
.table-placeholder { padding: 20px !important; color: var(--text-muted) !important; font-family: var(--font-body) !important; font-size: 0.82rem !important; }

/* 底部状态栏 */
.status-bar {
  margin-top: 14px;
  height: 32px;
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  padding: 0 14px;
  gap: 18px;
  font-size: 0.72rem;
  color: var(--text-muted);
}
.status-item { display: flex; align-items: center; gap: 5px; white-space: nowrap; }
.status-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
.status-dot.green { background: #27ae60; box-shadow: 0 0 5px rgba(39,174,96,0.4); }
.status-dot.gray { background: #bbb; }

/* ---- 响应式 ---- */
@media screen and (max-width: 960px) {
  .main-body { flex-direction: column; }
  .sidebar { width: 100%; }
  .video-container { min-height: 300px; }
  .status-bar { flex-wrap: wrap; height: auto; padding: 8px 14px; gap: 10px; }
}
</style>
